import React, { useEffect, useState } from 'react';
import { motion, useAnimation, useMotionValue, useSpring } from 'framer-motion';
import Mat3D from './Mat3D';

const ShowcaseSection = () => {
  const controls = useAnimation();
  const [activeView, setActiveView] = useState('intro');

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const springConfig = { damping: 40, stiffness: 200 };
  const rotateXMouse = useSpring(0, springConfig);
  const rotateYMouse = useSpring(0, springConfig);

  const mobileViews = {
    intro: { rotateX: 78, rotateY: -8, rotateZ: -12, y: 70, scale: 0.72, transition: { duration: 0 } },
    isometric: { rotateX: 46, rotateY: -10, rotateZ: -24, y: 64, z: 0, scale: 0.9, transition: { type: 'spring', damping: 22, stiffness: 45 } }
  };

  const desktopViews = {
    intro: { rotateX: 72, rotateY: -10, rotateZ: -16, y: 80, scale: 0.7, transition: { duration: 0 } },
    isometric: { rotateX: 44, rotateY: -12, rotateZ: -28, y: 80, z: 0, scale: 0.92, transition: { type: 'spring', damping: 22, stiffness: 45 } }
  };

  useEffect(() => {
    const sequence = async () => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      const views = isMobile ? mobileViews : desktopViews;

      await controls.start(views.intro);
      await new Promise(r => setTimeout(r, 400));
      setActiveView('isometric');
      await controls.start(views.isometric);
    };
    sequence();
  }, [controls]);

  const handleMouseMove = (e) => {
    if (activeView !== 'isometric') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    rotateYMouse.set(x * 15);
    rotateXMouse.set(y * -15);
  };

  return (
    <section 
      className="relative w-full h-screen flex items-center justify-center overflow-hidden"
      onMouseMove={handleMouseMove}
      style={{ perspective: '2000px' }}
    >
      <div className="absolute inset-0 floor-grid pointer-events-none opacity-40" />
      
      {/* Dynamic Headlines Matched to Script */}
      <motion.div 
        className="absolute top-20 md:top-1/4 text-center z-50 pointer-events-none px-4"
        animate={{ opacity: activeView === 'isometric' ? 1 : 0, y: activeView === 'isometric' ? 0 : -20 }}
      >
        <span className="text-cyan-400 font-mono text-[10px] tracking-[0.5em] uppercase mb-4 block">Residential</span>
        <h2 className="text-white text-4xl md:text-6xl font-extralight tracking-tighter">Perfect For Your Home.</h2>
        <p className="text-white/40 mt-4 md:mt-6 font-mono text-xs md:text-sm tracking-[0.25em] md:tracking-widest">HOMES, WORKPLACES & ENTRYWAYS</p>
      </motion.div>

      {/* Stats cards shown after mat reaches flat stage */}
      <div className="absolute top-14 left-0 right-0 z-50 pointer-events-none px-4 hidden md:flex justify-center">
        <motion.div
          className="w-full max-w-4xl"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: activeView === 'isometric' ? 1 : 0, y: activeView === 'isometric' ? 0 : -20 }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-cyan-400 font-mono text-[10px] tracking-[0.4em] uppercase mb-4 block text-center">Why SaniQ</span>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-center backdrop-blur-sm">
              <p className="text-cyan-400 text-lg md:text-xl font-semibold">99.9%</p>
              <p className="text-white/65 text-[11px] uppercase tracking-wider mt-1">Germs Eliminated</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-center backdrop-blur-sm">
              <p className="text-cyan-400 text-lg md:text-xl font-semibold">2-3s</p>
              <p className="text-white/65 text-[11px] uppercase tracking-wider mt-1">Auto Disinfection</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-center backdrop-blur-sm">
              <p className="text-cyan-400 text-lg md:text-xl font-semibold">96%</p>
              <p className="text-white/65 text-[11px] uppercase tracking-wider mt-1">Shoes Carry Bacteria</p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div className="preserve-3d" animate={controls}>
        <motion.div className="preserve-3d" style={{ rotateX: rotateXMouse, rotateY: rotateYMouse }}>
          <Mat3D />
        </motion.div>
      </motion.div>

      {/* Interaction Hint */}
      <motion.div 
        className="absolute bottom-10 flex flex-col items-center gap-2 opacity-40"
        initial={{ opacity: 0 }}
        animate={{ opacity: activeView === 'isometric' ? 0.4 : 0 }}
      >
        <div className="w-[1px] h-12 bg-gradient-to-b from-cyan-500 to-transparent" />
        <span className="text-[10px] uppercase tracking-widest text-white md:hidden">Scroll up to explore</span>
        <span className="hidden text-[10px] uppercase tracking-widest text-white md:block">Move Mouse to Explore</span>
      </motion.div>
    </section>
  );
};

export default ShowcaseSection;